// What someone talking to Arlo HQ wants. The default is an answer: people
// mostly talk to it to ask. It only goes straight to logging for a clear,
// one-off report of something that just happened ("he's just done a wee
// outside", "wee and poo outside") or when told to ("log a wee"). Anything
// else is answered — and if it mentions something worth logging ("he was
// sick in the car"), that's offered under the answer, never instead of it.

import { parseLogText, type LogProposal } from './parse.ts';

export type Intent =
  /** A one-off report, or "log …": confirm and log it. */
  | { kind: 'log'; proposals: LogProposal[] }
  /** "Log …" the simple rules couldn't read: worth asking the AI to parse. */
  | { kind: 'parse' }
  /** Everything else: answer it, offering any report in it as an optional log. */
  | { kind: 'ask'; alsoLog: LogProposal[] };

const FILLER = /^(hey |ok |okay |so |um |erm |right |arlo hq,? |arlo,? )+/i;

/** Told to log: "log a wee", "record his dinner", "note that he…". */
const COMMAND = /^(log|record|add|note|mark|track|save)\b/i;

/** It happened just now (or at a time): a moment to log, not a pattern to ask about. */
const MOMENT = /\b(just|just now|right now|ago|a minute|a few minutes|at \d{1,2}(?::\d\d)?\s*(?:am|pm)?|earlier|(?:from|between|till|until) (?:half )?\d+|(?:napped|slept|played|walked)\b[^.?]*\bfor (?:an?|half an|\d+) (?:hours?|hrs?|min(?:ute)?s?))\b/i;

/** A habit, a worry or a plan — something to talk about, not a moment to log. */
const GENERAL =
  /\b(keeps?|keeping|always|every time|everytime|whenever|when he|when we|a lot|lots|often|all the time|again|still|never|usually|sometimes|most|any ?time|tends? to|seems?|might|maybe|worried|worry|worrying|concerned|need|needs|should|could|would|will|going to|supposed|normal|ok\b|okay|bit|a little|why|how|what|which|when|where|who|is it|is that|is this|is he|do we|does he|can we|can he|tips?|advice|help|hates?|loves?|likes?|doesn'?t|don'?t|won'?t|isn'?t|hasn'?t|haven'?t|didn'?t|not|no)\b/i;

/** Words that are only ever about an event — so a short fragment of them ("wee and poo outside") is a report. */
const EVENT_WORDS = /^(?:\s|and|then|a|an|his|he|he's|hes|had|did|done|has|was|outside|inside|in|on|the|wees?|wee'?d|weed|pees?|peed|poos?|pooed|poop|nap|napping|asleep|sleep|woke|awake|up|breakfast|lunch|dinner|tea|meal|ate|eaten|all|half|some|water|drink|walk|walked|garden|street|park|rug|mat|floor|crate|bed|pad|kitchen|hall|flat|now|just)+$/i;

/** Who it's about: a report says "he", "Arlo", "we" — a topic ("crate training") doesn't. */
const SUBJECT = /\b(he|he's|hes|him|his|arlo|arlo's|we|we've|i)\b/i;

export function readIntent(text: string, now: number = Date.now()): Intent {
  // Phones type curly apostrophes: "hasn’t" must read as "hasn't".
  const t = text.replace(/[’‘]/g, "'").trim().replace(FILLER, '').replace(/[.!]+$/, '');
  const found = parseLogText(t, now);
  const words = t.split(/\s+/).filter(Boolean).length;
  const asked = /\?\s*$/.test(t);

  if (COMMAND.test(t)) return found.length ? { kind: 'log', proposals: found } : { kind: 'parse' };
  if (found.length && !asked && !GENERAL.test(t)) {
    // "He's just done a wee outside", "ate all his breakfast at 8".
    if (MOMENT.test(t) && words <= 16) return { kind: 'log', proposals: found };
    // "Wee and poo outside", "breakfast all eaten": a fragment made only of event words.
    if (words <= 6 && EVENT_WORDS.test(t)) return { kind: 'log', proposals: found };
  }
  // Answer — and if it reports a moment ("he was sick in the car"), offer that as a log too.
  // Not for habits and topics ("he keeps weeing inside", "crate training"): there's nothing to log.
  const reportPart = t.split(/\b(?:what|how|why|should|is it|is that|can we|do we|any tips|any advice|help)\b/i)[0];
  const habit = /\b(keeps?|always|every time|whenever|when he|when we|usually|tends? to|a lot|all the time|need|needs|should|will|going to)\b/i.test(reportPart);
  const negated = /\b(hasn'?t|haven'?t|didn'?t|won'?t|isn'?t|doesn'?t|not|never|no)\b/i.test(reportPart);
  const offer = !habit && SUBJECT.test(reportPart) ? parseLogText(reportPart, now) : [];
  // "Didn't eat his breakfast" is still a meal worth logging; "hasn't pooed" isn't a poo.
  return { kind: 'ask', alsoLog: negated ? offer.filter((p) => p.type === 'meal') : offer };
}
